import { Component, ElementRef, Input, OnInit, Renderer2, ViewEncapsulation } from "@angular/core";
import { Store } from "@ngxs/store";
import { ViewState } from "@vattenfall/core";
import { coerceBooleanProperty } from "@vattenfall/util";
import { intersection } from "ramda";

import { MySelectElectricityProduct } from "./my-select-electricity-product.actions";
import { MySelectElectricityProductExtendedModel } from "./my-select-electricity-product.model";
const getViewStates = (productId: string) => ({
  error: new MySelectElectricityProduct.Errorred(productId),
  loading: new MySelectElectricityProduct.Loading(productId),
  completedVariable: new MySelectElectricityProduct.Completed(productId, {
    productId: "1",
    agreementName: "Ilmasto Mix",
    monthlyBaseFee: {
      value: 16.5,
      unit: "EURO/MONTH",
    },
    discountedMonthlyBaseFee: {
      value: 2.43,
      unit: "EURO/MONTH",
    },
    electricityPrice: {
      value: 0.5975,
      unit: "EURO_CENT/KWH",
    },
    contractLength: {
      value: 24,
      unit: "MONTH",
    },
    discountLength: {
      value: 24,
      unit: "MONTH",
    },
    productType: "variable",
    hideMonthlyCost: false,
    energySourceName: "Aurinko alkuperä",
    originFee: { value: 0.5, unit: "EURO/MONTH" },
  }),
  completedFixed: new MySelectElectricityProduct.Completed(productId, {
    productId: "2",
    agreementName: "Tuntisähkö Mix",
    monthlyBaseFee: {
      value: 2.95,
      unit: "EURO/MONTH",
    },
    discountedMonthlyBaseFee: {
      value: 0.43,
      unit: "EURO/MONTH",
    },
    electricityPrice: {
      value: 0.6975,
      unit: "EURO_CENT/KWH",
    },
    contractLength: {
      value: 24,
      unit: "MONTH",
    },
    discountLength: {
      value: 12,
      unit: "MONTH",
    },
    productType: "fixed",
    hideMonthlyCost: false,
    energySourceName: "Vesi alkuperä",
    originFee: { value: 0.5, unit: "EURO/MONTH" },
  }),
  completedNoDiscount: new MySelectElectricityProduct.Completed(productId, {
    productId: "2",
    agreementName: "Tuntisähkö Mix",
    monthlyBaseFee: {
      value: 2.95,
      unit: "EURO/MONTH",
    },
    discountedMonthlyBaseFee: null as any,
    electricityPrice: {
      value: 0.6975,
      unit: "EURO_CENT/KWH",
    },
    contractLength: {
      value: 24,
      unit: "MONTH",
    },
    productType: "fixed",
    hideMonthlyCost: false,
    energySourceName: "Tuuli alkuperä",
    originFee: { value: 0.5, unit: "EURO/MONTH" },
  }),
  completedMontlyCostHidden: new MySelectElectricityProduct.Completed(productId, {
    productId: "2",
    agreementName: "Tuntisähkö Mix",
    monthlyBaseFee: {
      value: 2.95,
      unit: "EURO/MONTH",
    },
    discountedMonthlyBaseFee: {
      value: 0.43,
      unit: "EURO/MONTH",
    },
    electricityPrice: {
      value: 0.6975,
      unit: "EURO_CENT/KWH",
    },
    contractLength: {
      value: 24,
      unit: "MONTH",
    },
    discountLength: {
      value: 12,
      unit: "MONTH",
    },
    productType: "fixed",
    hideMonthlyCost: true,
    energySourceName: "Täysydin alkuperä",
    originFee: { value: 0.5, unit: "EURO/MONTH" },
  }),
  completedSpotGuard: new MySelectElectricityProduct.Completed(productId, {
    productId: "1",
    agreementName: "Optimi Pörssisähko + hinnankiinnitysturva",
    monthlyBaseFee: { value: 5.95, unit: "EURO/MONTH" },
    electricityPrice: { value: 0.5, unit: "EURO_CENT/KWH" },
    spotGuardFee: { value: 4.9, unit: "EURO/MONTH" },
    contractLength: { value: 24, unit: "MONTH" },
    productType: "spot",
    hideMonthlyCost: true,
    hideElectricityPriceRow: false,
  }),
});

@Component({
  selector: "my-select-electrical-product",
  templateUrl: "./my-select-electricity-product.component.html",
  styleUrls: ["./my-select-electricity-product.component.scss"],
  encapsulation: ViewEncapsulation.ShadowDom,
  host: {
    "[class.hide]": "error || hide",
  },
})
export class MySelectElectricityProductComponent
  extends ViewState(MySelectElectricityProductExtendedModel)
  implements OnInit
{
  /**
   * Product identifier
   */
  @Input("productid") productId!: string;
  /**
   * Whether the product is filterable or not
   */
  @Input("filterable") filterable!: string;
  /**
   * The URL to redirect to after a product has been selected
   */
  @Input("redirecturl") redirectUrl!: string;
  /**
   * The selected consumption code
   */
  @Input("consumptioncode") consumptionCode!: string;
  /**
   * The customers estimated yearly consumption in kWh
   */
  @Input("estimatedkwhconsumption") estimatedKwhConsumption!: number;
  /**
   * The campaign code for product
   */
  @Input("campaigncode") campaignCode!: string;
  /**
   * The marketing code to track campaign
   */
  @Input("marketingcode") marketingCode!: string;

  /**
   * Whether the product should be filtered out or not
   */
  public hide = true;

  constructor(public store: Store, public elementRef: ElementRef, public renderer: Renderer2) {
    super();
  }

  ngOnInit() {
    if (!this.productId || !this.estimatedKwhConsumption) {
      console.error(
        `[ Vattenfall Elements – MySelectElectricityProductComponent ] Could not initialize the component. Required attribute 'productid' or 'estimatedkwhconsumption' is missing.`
      );
      this.error = true;
    } else {
      super.viewStateInit(
        {
          [`mySelectElectricityProduct.${this.productId}.error`]: null,
          [`mySelectElectricityProduct.${this.productId}.loading`]: null,
          [`mySelectElectricityProduct.${this.productId}.productType`]: null,
          [`mySelectElectricityProduct.${this.productId}.agreementName`]: null,
          [`mySelectElectricityProduct.${this.productId}.monthlyBaseFee`]: null,
          [`mySelectElectricityProduct.${this.productId}.discountedMonthlyBaseFee`]: null,
          [`mySelectElectricityProduct.${this.productId}.electricityPrice`]: null,
          [`mySelectElectricityProduct.${this.productId}.contractLength`]: null,
          [`mySelectElectricityProduct.${this.productId}.discountLength`]: null,
          [`mySelectElectricityProduct.${this.productId}.hideMonthlyCost`]: null,
          [`mySelectElectricityProduct.${this.productId}.energySourceName`]: null,
          [`mySelectElectricityProduct.${this.productId}.originFee`]: null,
          [`mySelectElectricityProduct.${this.productId}.spotGuardFee`]: null,
          [`mySelectElectricityProduct.${this.productId}.hideElectricityPriceRow`]: null,
          "electricalProductsFilter.filter": (filter: string = "") => {
            // We split the filter string for the cases where multiple values are selected as filter condition (ex: 'variable|spot')
            // Ex ["variable", "spot"] or ["fixed"]
            const selectedFilters = filter.trim().split("|");
            // The array of values that when selected in the filter option, this product will show.
            // ex ["*", "fixed"] or ["*", "variable"] or ["*", "spot"]
            const positiveFilters = ["*", this.productType];
            // If the selected filters arrayand the positive array do not intersect, we should hide the product.
            const shouldHide = intersection(positiveFilters, selectedFilters).length === 0;
            // Apply this filter only if the product is filterable.
            const isFilterable = filter && coerceBooleanProperty(this.filterable);
            // Apply filter only if the product is fitlerable.
            isFilterable ? (this.hide = shouldHide) : (this.hide = false);
          },
        },
        {
          store: this.store,
          elementRef: this.elementRef,
          renderer: this.renderer,
        },
        getViewStates(this.productId)
      );
      this.store.dispatch(new MySelectElectricityProduct.Fetch(this.productId, this.campaignCode));
    }
  }

  /**
   * Selects a product, adds it to cart and redirects to the specified redirect URL
   */
  onSelected() {
    this.store.dispatch(
      new MySelectElectricityProduct.Selected(
        this.redirectUrl,
        this.productId,
        this.campaignCode,
        this.marketingCode,
        this.estimatedKwhConsumption,
        this.consumptionCode,
        this.productType,
        this.agreementName,
        this.electricityPrice
      )
    );
  }
  /**
   *
   * @param name - name of the slot
   * @returns - true if the slot is empty
   */
  isEmptySlot(name: string) {
    const slot = this.elementRef?.nativeElement?.querySelector(`[slot="${name}"]`);
    return slot?.innerHTML?.trim() === "";
  }
}
